import { useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { addError } from "@wayflyer/blackbox-fe";
import type {
  BankAccountGroupSchema,
  BankConnectionSchema,
  LogoLoadingState,
  UngroupedStatementSchema,
} from "@wayflyer/customer-bankconnect";
import { useBankConnectionLogoResolvers, useBankConnectionLogos } from "@wayflyer/customer-bankconnect";
import { useCallback, useState } from "react";
import { useErrorModal } from "shared-ui";
import { useCustomerAppConfig } from "@wayflyer/undercarriage";

import { useSubmitUnsubmittedBankStatements } from "../../../hooks/use-submit-unsubmitted-bank-statements";
import {
  groupedBankingSourcesQueryOptions,
  invalidateBankingSourceQueries,
} from "../../../queries/bank-connect/queries";

import { useBankControllerContext } from "./bank-controller-context";
import { hasExtractionTimedOut, useExtractionTimeoutClock } from "./use-statement-extraction-timeout";

// Deliberately excludes legacy-only fields (bankingConnections, hasBankingConnections,
// bankingStatementsGroups, statementCount, stepCompleted, fileUploadProps) - those derive from
// the old flat sources query, so exposing them here risks silently disagreeing with the grouped view.
export type UseBankingAccountsPageControllerResult = Pick<
  ReturnType<typeof useBankControllerContext>,
  | "bankConnectProps"
  | "countryForm"
  | "countryOptions"
  | "defaultCountry"
  | "isConnectionUnsupportedCountry"
  | "isConnecting"
  | "handleConnect"
  | "providerName"
  | "ready"
  | "requiredStatementMonths"
  | "requireConnection"
  | "isUkIncorporated"
  | "isConnectionsSlideoutOpen"
  | "closeConnectionsSlideout"
  | "shouldConfirmAccountSelection"
  | "confirmAccountsBankConnections"
> & {
  customerDomain: string;
  handleNext: (onTerminalStep?: () => void | Promise<void>) => Promise<void>;
  isSubmitting: boolean;
  accountGroups: BankAccountGroupSchema[];
  ungroupedConnections: BankConnectionSchema[];
  ungroupedStatements: UngroupedStatementSchema[];
  hasAnyBankingConnection: boolean;
  hasProvidedBankingData: boolean;
  logoLoadingStates: LogoLoadingState;
  invalidateBankingSources: () => Promise<void>;
  invalidateAllBankingSources: () => ReturnType<typeof invalidateBankingSourceQueries>;
  isRefetchingBankingSources: boolean;
};

export const useBankingAccountsPageController = (): UseBankingAccountsPageControllerResult => {
  // TODO: pull this state in directly so the hook no longer depends on useBankControllerContext.
  const {
    bankConnectProps,
    basiqClient,
    countryForm,
    countryOptions,
    defaultCountry,
    isConnectionUnsupportedCountry,
    isConnecting,
    handleConnect,
    handleNext: goToNextStep,
    providerName,
    ready,
    isSubmitting: isNavigating,
    requiredStatementMonths,
    requireConnection,
    isUkIncorporated,
    isConnectionsSlideoutOpen,
    closeConnectionsSlideout,
    shouldConfirmAccountSelection,
    confirmAccountsBankConnections,
  } = useBankControllerContext();

  const {
    data: { customerDomain },
  } = useCustomerAppConfig();
  const queryClient = useQueryClient();
  const { openErrorModal } = useErrorModal();
  const submitUnsubmittedBankStatements = useSubmitUnsubmittedBankStatements(customerDomain);
  const [isSubmittingStatements, setIsSubmittingStatements] = useState(false);

  // This page has no manual submit action, so unsubmitted statements are submitted here before
  // continuing - not in the shared bank controller, which the legacy page also uses.
  const handleNext = useCallback(
    async (onTerminalStep?: () => void | Promise<void>) => {
      setIsSubmittingStatements(true);
      try {
        await submitUnsubmittedBankStatements();
      } catch (e: any) {
        setIsSubmittingStatements(false);
        openErrorModal({ variant: "submit-error", error: e });
        addError(e, { message: "banking-accounts-page-controller: submit error" });
        return;
      }
      setIsSubmittingStatements(false);
      await goToNextStep(onTerminalStep);
    },
    [submitUnsubmittedBankStatements, goToNextStep, openErrorModal]
  );

  const { data: groupedBankingSources, isFetching: isRefetchingBankingSources } = useSuspenseQuery({
    ...groupedBankingSourcesQueryOptions(customerDomain),
    refetchOnWindowFocus: false,
  });
  const { accountGroups, ungroupedConnections, ungroupedStatements: allUngroupedStatements } = groupedBankingSources;
  const now = useExtractionTimeoutClock(allUngroupedStatements);

  // Excludes statements still being extracted (created or processing), unless they've timed out
  const ungroupedStatements = allUngroupedStatements.filter(
    (statement) =>
      (statement.extractionState !== "created" && statement.extractionState !== "processing") ||
      hasExtractionTimedOut(statement, now)
  );

  const invalidateBankingSources = useCallback(
    () =>
      queryClient.invalidateQueries({
        queryKey: groupedBankingSourcesQueryOptions(customerDomain).queryKey,
      }),
    [queryClient, customerDomain]
  );

  // Unlike invalidateBankingSources above, this also covers the workflow tracker's own
  // completion-status query - needed after an action (move/remove) that can change whether this
  // step now reads as complete, not just which statements sit where.
  const invalidateAllBankingSources = useCallback(
    () => invalidateBankingSourceQueries(queryClient, customerDomain),
    [queryClient, customerDomain]
  );

  const allBankConnections: BankConnectionSchema[] = [
    ...ungroupedConnections,
    ...accountGroups.flatMap((group) => group.connectionMembers.map((member) => member.connection)),
  ];
  const hasAnyBankingConnection =
    ungroupedConnections.length > 0 || accountGroups.some((group) => group.connectionMembers.length > 0);

  const hasProvidedBankingData =
    hasAnyBankingConnection ||
    allUngroupedStatements.length > 0 ||
    accountGroups.some((group) => group.statementMembers.length > 0);

  const logoResolvers = useBankConnectionLogoResolvers({ customerSlug: customerDomain, basiqClient });
  const { logoLoadingStates } = useBankConnectionLogos({
    bankConnections: allBankConnections,
    resolvers: logoResolvers,
  });

  return {
    customerDomain,
    bankConnectProps,
    countryForm,
    countryOptions,
    defaultCountry,
    isConnectionUnsupportedCountry,
    isConnecting,
    handleConnect,
    handleNext,
    providerName,
    ready,
    isSubmitting: isNavigating || isSubmittingStatements,
    requiredStatementMonths,
    requireConnection,
    isUkIncorporated,
    isConnectionsSlideoutOpen,
    closeConnectionsSlideout,
    shouldConfirmAccountSelection,
    confirmAccountsBankConnections,
    accountGroups,
    ungroupedConnections,
    ungroupedStatements,
    hasAnyBankingConnection,
    hasProvidedBankingData,
    logoLoadingStates,
    invalidateBankingSources,
    invalidateAllBankingSources,
    isRefetchingBankingSources,
  };
};
